import { schemas } from '#oaf_schema.registry';
import { logger } from '~/modules/utilities/logger';
import { publisherStore } from '../stores/publisher/publisherStore';
import { missingListingSchemaStepErrorMap } from './errorUtilities';
import { hasJapaneseCompanyDetails } from './japanesePublisherUtilities';
import { merge, reduceValues } from './objectUtilities';
import { NON_FORCE_PACKAGE_SOLUTION_TYPES, TECHNOLOGY_EXTENSION_MAP, TECHNOLOGY_SOLUTION_EXTENSION_MAP, TECHNOLOGY_SOLUTION_MAP, TECHNOLOGY_TEST_DRIVE_MAP, getPlugin, isConsultantType, isLinkType, isTableauType, validateCampaignId, validateOrgId, validateUrl } from './schemaUtilities';
const FALLBACK_STEP = Symbol();
const SHARED_STEPS_ERROR_MAP = {
  'listing/plugins/SupportInformation': '1',
  'listing/plugins/Demo': '3-2',
  'listing/plugins/Content': '3-2',
  'listing/plugins/LogoSet': '3-2',
  'listing/plugins/Carousel': '3-2',
  japaneseCompanyDetails: 'japaneseCompanyDetails'
};
const SCHEMA_ID_TO_STEP_ERROR_MAP = {
  [FALLBACK_STEP]: {
    ...SHARED_STEPS_ERROR_MAP,
    ...reduceValues(TECHNOLOGY_SOLUTION_EXTENSION_MAP, (accumulator, key) => {
      accumulator[key] = '4';
      return accumulator;
    }),
    ...reduceValues(TECHNOLOGY_TEST_DRIVE_MAP, (accumulator, key) => {
      accumulator[key] = '5';
      return accumulator;
    })
  },
  'force/package': {
    ...SHARED_STEPS_ERROR_MAP,
    businessNeeds: '1',
    'listing/extensions/force/listings/Listing/extensions/PackageListing': '4',
    'listing/plugins/LeadTrialInformation': '5',
    'listing/plugins/TrialTemplate': '5',
    'listing/extensions/force/listings/Listing/extensions/PackageListing/TestDrive': '5'
  },
  'force/copilot': {
    ...SHARED_STEPS_ERROR_MAP,
    businessNeeds: '1',
    'listing/extensions/force/listings/CopilotListing': '1',
    'listing/extensions/force/listings/Listing/extensions/CopilotListing': '4',
    'listing/plugins/LeadTrialInformation': '5',
    'listing/plugins/TrialTemplate': '5'
  },
  consultant: {
    'listing/plugins/SupportInformation': '1',
    'listing/plugins/Demo': '2-2',
    'listing/plugins/Content': '2-2',
    'listing/plugins/LogoSet': '2-2',
    'listing/plugins/Carousel': '2-2',
    'listing/plugins/LeadTrialInformation': '3'
  },
  'tableau/accelerator': {
    ...SHARED_STEPS_ERROR_MAP,
    'listing/extensions/tableau/listings/Listing/extensions/AcceleratorListing': '4',
    'listing/plugins/LeadTrialInformation': '5'
  }
};
export function getStepErrorMap(listingType) {
  const schemaStepErrorMap = SCHEMA_ID_TO_STEP_ERROR_MAP[listingType];
  if (schemaStepErrorMap) {
    return schemaStepErrorMap;
  }
  const fallbackSchemaStepErrorMap = SCHEMA_ID_TO_STEP_ERROR_MAP[FALLBACK_STEP];
  /* istanbul ignore next: this should never happen as long as FALLBACK_STEP exists in SCHEMA_ID_TO_STEP_ERROR_MAP */
  if (!fallbackSchemaStepErrorMap) {
    missingListingSchemaStepErrorMap(`Cannot find fallback schema error map for '${listingType}'`);
    return undefined;
  }
  return fallbackSchemaStepErrorMap;
}
export function getExtension(state, extensionType) {
  var _state$extensions, _state$extensions$fin;
  return state === null || state === void 0 ? void 0 : (_state$extensions = state.extensions) === null || _state$extensions === void 0 ? void 0 : (_state$extensions$fin = _state$extensions.find(test => test.extensionType === extensionType)) === null || _state$extensions$fin === void 0 ? void 0 : _state$extensions$fin.data;
}
export function selectExtensionListing(type, listing) {
  return getExtension(listing, TECHNOLOGY_EXTENSION_MAP[type]);
}
export function selectSolutionExtension(type, forceListing) {
  return getExtension(forceListing, TECHNOLOGY_SOLUTION_EXTENSION_MAP[type]);
}
export function selectSupportInfo(listing) {
  return getPlugin(listing, 'listing/plugins/SupportInformation');
}
export function selectLogoSet(listing) {
  return getPlugin(listing, 'listing/plugins/LogoSet');
}
export function selectContent(listing) {
  return getPlugin(listing, 'listing/plugins/Content');
}
export function selectCarousel(listing) {
  return getPlugin(listing, 'listing/plugins/Carousel');
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function selectLeadTrialInformation(listing) {
  return getPlugin(listing, 'listing/plugins/LeadTrialInformation');
}
export function selectTrialTemplate(listing) {
  return getPlugin(listing, 'listing/plugins/TrialTemplate');
}
export function generateStepMetadata(valueSelector, nameSelector, schemaSelector, options = {
  minItems: 0
}) {
  return {
    getName: nameSelector,
    getValue: valueSelector,
    getSchema: () => {
      var _schemas$schemaSelect;
      const schema = (_schemas$schemaSelect = schemas[schemaSelector()]) === null || _schemas$schemaSelect === void 0 ? void 0 : _schemas$schemaSelect.get();
      return merge(schema === null || schema === void 0 ? void 0 : schema.properties[nameSelector()], options.minItems > 0 ? {
        minItems: options.minItems
      } : {});
    },
    isRequired: () => {
      var _schemas$schemaSelect2;
      const schema = (_schemas$schemaSelect2 = schemas[schemaSelector()]) === null || _schemas$schemaSelect2 === void 0 ? void 0 : _schemas$schemaSelect2.get();
      return schema && 'required' in schema && schema.required.includes(nameSelector());
    }
  };
}
export function createJapaneseCompanyDetails(extensionListing) {
  return [{
    getName: () => 'japaneseCompanyDetails',
    checkComplete: () => extensionListing.marketplace !== 'AppExchange_Japanese' || hasJapaneseCompanyDetails(publisherStore.getState().publisher)
  }];
}
export function createFillInTheBasicsFields(listing, extensionListing) {
  const getExtensionSchemaId = () => TECHNOLOGY_EXTENSION_MAP[listing.technology];
  let sharedSteps = [generateStepMetadata(() => listing.name, () => 'name', getBaseListingSchemaId), generateStepMetadata(() => listing.description, () => 'description', getBaseListingSchemaId), generateStepMetadata(() => extensionListing.languages, () => 'languages', getExtensionSchemaId, {
    minItems: 1
  }), generateStepMetadata(() => {
    var _selectSupportInfo;
    return (_selectSupportInfo = selectSupportInfo(listing)) === null || _selectSupportInfo === void 0 ? void 0 : _selectSupportInfo.businessEmail;
  }, () => 'businessEmail', getSupportInformationSchemaId)];
  if (extensionListing && extensionListing.isDiverseOwnedBusiness) {
    sharedSteps.push({
      getName: () => 'diverseOwnedBusinessUrl',
      checkComplete: () => Boolean(extensionListing.diverseOwnedBusinessURL && validateUrl(extensionListing.diverseOwnedBusinessURL))
    });
  }
  if (isConsultantType(listing.technology)) {
    return [...sharedSteps, generateStepMetadata(() => extensionListing.learnMoreURL, () => 'learnMoreURL', getExtensionSchemaId)];
  } else if (extensionListing && extensionListing.isAccessibleSolution) {
    const nonConsultantExtension = extensionListing;
    sharedSteps.push({
      getName: () => 'accessibleSolutionURL',
      checkComplete: () => Boolean(nonConsultantExtension.accessibleSolutionURL && validateUrl(nonConsultantExtension.accessibleSolutionURL))
    });
  }
  if (!isConsultantType(listing.technology)) {
    sharedSteps = sharedSteps.concat([generateStepMetadata(() => extensionListing.targetUserPersona, () => 'targetUserPersona', getExtensionSchemaId), generateStepMetadata(() => extensionListing.supportedIndustries, () => 'supportedIndustries', getExtensionSchemaId), generateStepMetadata(() => extensionListing.productsSupported, () => 'productsSupported', getExtensionSchemaId), generateStepMetadata(() => extensionListing.industries, () => 'industries', getExtensionSchemaId), {
      getName: () => 'businessNeeds',
      checkComplete: () => Boolean(validateBusinessNeeds(listing.businessNeeds))
    }]);
  }
  if (isLinkType(TECHNOLOGY_SOLUTION_MAP[listing.technology]) || isTableauType(listing.technology)) {
    return [...sharedSteps, generateStepMetadata(() => extensionListing.productsRequired, () => 'productsRequired', getExtensionSchemaId, {
      minItems: 1
    })];
  }
  return [...sharedSteps, generateStepMetadata(() => extensionListing.productsRequired, () => 'productsRequired', getExtensionSchemaId, {
    minItems: 1
  }), generateStepMetadata(() => extensionListing.editions, () => 'editions', getExtensionSchemaId, {
    minItems: 1
  }), {
    getName: () => 'supportedFeatures',
    checkComplete: () => extensionListing.isSalesforceShield || extensionListing.isMultiCurrency || extensionListing.isPersonAccounts || extensionListing.isLightningReady || extensionListing.isSalesforce1Mobile || extensionListing.isLightningComponent || extensionListing.isSalesProgram || extensionListing.isEinsteinAgent || false
  }];
}
export function createDescribeYourSolutionFields(listing, extensionListing) {
  const getExtensionSchemaId = () => TECHNOLOGY_EXTENSION_MAP[listing.technology];
  const sharedSteps = [generateStepMetadata(() => listing.seoTitle, () => 'seoTitle', getBaseListingSchemaId), generateStepMetadata(() => listing.fullDescription, () => 'fullDescription', getBaseListingSchemaId), generateStepMetadata(() => extensionListing.searchBlurb, () => 'searchBlurb', getExtensionSchemaId), {
    getName: () => 'highlights',
    checkComplete: () => extensionListing.highlights && extensionListing.highlights.length >= 1 && extensionListing.highlights.filter(str => str.trim() === '').length === 0 || false
  }];
  if (isConsultantType(listing.technology)) {
    return sharedSteps;
  }
  return [...sharedSteps, generateStepMetadata(() => extensionListing.licenseAgreement, () => 'licenseAgreement', getExtensionSchemaId)];
}
export function createIncludeVisualsFields(listing) {
  return [{
    getName: () => 'Logo',
    checkComplete: () => {
      var _selectLogoSet;
      return (_selectLogoSet = selectLogoSet(listing)) === null || _selectLogoSet === void 0 ? void 0 : _selectLogoSet.items.some(test => test.logoType === 'Logo' && test.mediaId);
    }
  }, {
    getName: () => 'Big Logo',
    checkComplete: () => {
      var _selectLogoSet2;
      return (_selectLogoSet2 = selectLogoSet(listing)) === null || _selectLogoSet2 === void 0 ? void 0 : _selectLogoSet2.items.some(test => test.logoType === 'Big Logo' && test.mediaId);
    }
  }, {
    getName: () => 'Screenshots and Videos',
    checkComplete: () => {
      var _selectCarousel, _selectCarousel$items;
      return Boolean((_selectCarousel = selectCarousel(listing)) === null || _selectCarousel === void 0 ? void 0 : (_selectCarousel$items = _selectCarousel.items) === null || _selectCarousel$items === void 0 ? void 0 : _selectCarousel$items.length);
    }
  }, generateStepMetadata(() => listing.enableMarketingUse, () => 'enableMarketingUse', getBaseListingSchemaId), {
    getName: () => 'Content',
    checkComplete: () => {
      var _selectContent;
      return !((_selectContent = selectContent(listing)) !== null && _selectContent !== void 0 && _selectContent.items.some(test => test.data.title && test.data.type && test.data.title.length > getContentTitleMaxLength() && (test.type === 'File' || test.type === 'Url' && test.data.url && (!validateUrl(test.data.url) || test.data.url.length > getContentUrlMaxLength()))));
    }
  }];
}
export function createSetPricingFields(listing, isPricingValid) {
  return [{
    getName: () => 'Pricing',
    checkComplete: () => {
      var _isPricingValid;
      return (_isPricingValid = isPricingValid === null || isPricingValid === void 0 ? void 0 : isPricingValid()) !== null && _isPricingValid !== void 0 ? _isPricingValid : false;
    }
  }];
}
export function createSetPricingApprovalsFields(isApproved) {
  return [{
    getName: () => 'Approvals',
    checkComplete: () => {
      var _isApproved;
      return (_isApproved = isApproved === null || isApproved === void 0 ? void 0 : isApproved()) !== null && _isApproved !== void 0 ? _isApproved : false;
    }
  }];
}
export function createRegisterYourPackageFields(isPackageRegistered) {
  return [{
    getName: () => 'PackageRegister',
    checkComplete: () => {
      var _isPackageRegistered;
      return (_isPackageRegistered = isPackageRegistered === null || isPackageRegistered === void 0 ? void 0 : isPackageRegistered()) !== null && _isPackageRegistered !== void 0 ? _isPackageRegistered : false;
    }
  }];
}
export function createLinkYourSolutionFields(listing, extensionListing, isSolutionValid) {
  return [{
    getName: () => 'installUrl',
    checkComplete: () => {
      if (listing && isTableauType(listing.technology)) {
        return true;
      }
      const extension = getExtension(extensionListing, TECHNOLOGY_SOLUTION_EXTENSION_MAP[listing.technology]);
      return !extension || extension.provisionFlow !== 'External' || validateUrl((extension === null || extension === void 0 ? void 0 : extension.installUrl) || '');
    }
  }, {
    getName: () => 'solutionId',
    checkComplete: () => {
      var _isSolutionValid;
      return (_isSolutionValid = isSolutionValid === null || isSolutionValid === void 0 ? void 0 : isSolutionValid()) !== null && _isSolutionValid !== void 0 ? _isSolutionValid : false;
    }
  }, {
    getName: () => 'selfAttestation',
    checkComplete: () => {
      return extensionListing && extensionListing.selfAttestation && Boolean(extensionListing.selfAttestationDate);
    }
  }];
}
export function createGrowYourBusinessFields(listing, extensionListing) {
  const steps = [{
    getName: () => 'listing/plugins/LeadTrialInformation',
    checkComplete: () => {
      const leadInfo = getPlugin(listing, 'listing/plugins/LeadTrialInformation');
      const hasPlugin = Boolean(leadInfo);
      const packageListing = selectSolutionExtension(listing.technology, extensionListing);
      if (hasPlugin) {
        let isRepresentativeComplete = true;
        let hasValidOrgIdWhenCampaignsEnabled = true;
        let hasValidCampaignIdsWhenCampaignsEnabled = true;
        if (leadInfo.leadsOrgID) {
          const hasValidOrgId = Boolean(validateOrgId(leadInfo.leadsOrgID));
          if (!hasValidOrgId) {
            return hasValidOrgId;
          }
        }
        if ((packageListing === null || packageListing === void 0 ? void 0 : packageListing.provisionFlow) === 'Representative') {
          isRepresentativeComplete = Boolean(leadInfo.leadsOrgID && validateOrgId(leadInfo.leadsOrgID)) && Boolean(leadInfo.enableInstallLeads);
        }
        if (leadInfo.enableDemoLeads || leadInfo.enableInstallLeads || leadInfo.enableTestDriveLeads || leadInfo.enableTrialLeads || leadInfo.enableLearnMoreLeads) {
          hasValidOrgIdWhenCampaignsEnabled = Boolean(leadInfo.leadsOrgID && validateOrgId(leadInfo.leadsOrgID));
          if (leadInfo.demoCampaignID) {
            hasValidCampaignIdsWhenCampaignsEnabled = Boolean(leadInfo.demoCampaignID && validateCampaignId(leadInfo.demoCampaignID));
          }
          if (leadInfo.learnMoreCampaignID) {
            hasValidCampaignIdsWhenCampaignsEnabled = hasValidCampaignIdsWhenCampaignsEnabled && Boolean(validateCampaignId(leadInfo.learnMoreCampaignID));
          }
          if (leadInfo.installCampaignID) {
            hasValidCampaignIdsWhenCampaignsEnabled = hasValidCampaignIdsWhenCampaignsEnabled && Boolean(validateCampaignId(leadInfo.installCampaignID));
          }
          if (leadInfo.testDriveCampaignID) {
            hasValidCampaignIdsWhenCampaignsEnabled = hasValidCampaignIdsWhenCampaignsEnabled && Boolean(validateCampaignId(leadInfo.testDriveCampaignID));
          }
          if (leadInfo.trialCampaignID) {
            hasValidCampaignIdsWhenCampaignsEnabled = hasValidCampaignIdsWhenCampaignsEnabled && Boolean(validateCampaignId(leadInfo.trialCampaignID));
          }
        }
        return isRepresentativeComplete && hasValidOrgIdWhenCampaignsEnabled && hasValidCampaignIdsWhenCampaignsEnabled;
      }
      return hasPlugin;
    }
  }];
  if (TECHNOLOGY_TEST_DRIVE_MAP[listing.technology]) {
    steps.push({
      getName: () => 'testDrive',
      checkComplete: () => {
        const extension = getExtension(extensionListing, TECHNOLOGY_SOLUTION_EXTENSION_MAP[listing.technology]);
        const testDriveExtension = getExtension(extension, TECHNOLOGY_TEST_DRIVE_MAP[listing.technology]);
        return !testDriveExtension || !testDriveExtension.enableTestDrive || testDriveExtension.enableTestDrive && Boolean(testDriveExtension.testDriveOrgID) && Boolean(testDriveExtension.testDriveUserName) && Boolean(testDriveExtension.testDrivePassword);
      }
    });
  }
  return steps;
}
export function hasListingValidSolution(listingWithSolution) {
  var _listingWithSolution$2;
  if (NON_FORCE_PACKAGE_SOLUTION_TYPES.includes(listingWithSolution.listing.technology)) {
    var _listingWithSolution$;
    //When it's an NON Force Package type we should check if the solution assosiated with the listing has an id populated
    return Boolean((_listingWithSolution$ = listingWithSolution.solution) === null || _listingWithSolution$ === void 0 ? void 0 : _listingWithSolution$.id);
  }
  const solution = (_listingWithSolution$2 = listingWithSolution.solution) === null || _listingWithSolution$2 === void 0 ? void 0 : _listingWithSolution$2.solution;
  const value = solution === null || solution === void 0 ? void 0 : solution.version.versionOptionsSecurityReviewed;
  // Catch the scenario where we have a package solution, but no version / security review flag.
  if (solution && value == null) {
    logger.error(`Malformed package solution, it's missing 'versionOptionsSecurityReviewed' field. Listing id='${listingWithSolution.listing.id}'`);
  }
  return Boolean(value);
}
function getBaseListingSchemaId() {
  return 'listing/BaseListing';
}
function getSupportInformationSchemaId() {
  return 'listing/plugins/SupportInformation';
}
function getContentTitleMaxLength() {
  return schemas['listing/plugins/Content/Items/ContentUrl'].get().properties.title.maxLength;
}
function getContentUrlMaxLength() {
  return schemas['listing/plugins/Content/Items/ContentUrl'].get().properties.url.maxLength;
}
function validateBusinessNeeds(businessNeeds) {
  if (businessNeeds) {
    let businessNeedsCount = 0;

    // Loop through all the keys, and add up how many L1s and L2s have been selected
    for (const levelOne of Object.keys(businessNeeds)) {
      const need = businessNeeds[levelOne];
      if (need == null) {
        continue;
      }
      businessNeedsCount += need.categories.length + (need.isSelected ? 1 : 0);
    }
    if (businessNeedsCount > 3) {
      return false;
    }
  }
  return true;
}
export function getMessages(_listing) {
  // TODO: Calculate error messages or find a way to do that
  /*
      const messages = {
          errors: [
              { step: 1, message: 'wow this is an Error' },
              { step: 2, message: 'wow this is an Error 2' }
          ],
          warnings: [{ step: 4, message: 'wow this is aWarning' }]
      };
  */
  return {};
}